import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import styled from 'styled-components';

import { Link } from '@components/link';
import { Stack } from '@components/stack';
import { Text } from '@components/text/';
import { lineClamp } from '@styles/layout-helpers';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import portalCardImage from './portal-card-image.svg';

export const PORTAL_CARD_IMAGE_HEIGHT = 180;
export const PORTAL_CARD_CONTENT_HEIGHT = 160;

export type PortalCardProps = {
  name: string;
  description: string;
  image?: string;
  link?: string;
  dataCy?: string;
  actions?: JSX.Element;
  imageDragProps?: SyntheticListenerMap;
  topIcons?: JSX.Element;
  isLink?: boolean;
  onClick?: () => void;
};

export const PortalCard = ({
  name,
  description,
  image,
  link,
  dataCy,
  actions,
  imageDragProps,
  topIcons,
  isLink = false,
  onClick,
}: PortalCardProps) => {
  const backgroundImage = image ? image : portalCardImage;
  const hasActions = Boolean(actions);
  return (
    <StyledCard
      data-cy={dataCy}
      data-testid="portal-card"
      $hasLink={Boolean(link) || isLink}
      as={link ? Link : 'div'}
      to={link}
      onClick={onClick}
    >
      <ImageContainer $isDraggable={Boolean(imageDragProps)} {...imageDragProps}>
        <StyledImage src={backgroundImage} alt="background" />
        {topIcons && <TopIconsWrapper>{topIcons}</TopIconsWrapper>}
      </ImageContainer>
      <ContentWrapper>
        <Stack space={1}>
          <Text
            text={name}
            font="bold18"
            style={{ ...lineClamp('-webkit-box', hasActions ? 1 : 2) }}
          />
          <Text
            text={description}
            font="regular14"
            style={{ ...lineClamp('-webkit-box', hasActions ? 2 : 3) }}
          />
        </Stack>
        {actions}
      </ContentWrapper>
    </StyledCard>
  );
};

const StyledCard = styled.div<{ $hasLink: boolean }>((p) => ({
  display: 'flex',
  position: 'relative',
  flexDirection: 'column',
  width: pxToRem(320),
  height: pxToRem(340),
  borderRadius: pxToRem(4),
  border: `${pxToRem(1)} solid #0000001A`,
  transition: 'all 300ms ease-out',
  overflow: 'hidden',
  backgroundColor: theme.color.backgroundLightDark,
  ...(p.$hasLink && {
    ':hover': {
      border: `${pxToRem(1)} solid #00000033`,
      boxShadow: `${pxToRem(8)} ${pxToRem(8)} 0 0 #CCDFFF`,
    },
  }),
  cursor: 'pointer',
}));

const ImageContainer = styled.div<{ $isDraggable: boolean }>((p) => ({
  position: 'relative',
  height: pxToRem(PORTAL_CARD_IMAGE_HEIGHT),
  ...(p.$isDraggable && {
    cursor: 'grab',

    '&:active': {
      cursor: 'grabbing',
    },
  }),
}));

const ContentWrapper = styled(Stack)({
  height: pxToRem(PORTAL_CARD_CONTENT_HEIGHT),
  padding: pxToRem(16),
  justifyContent: 'space-between',
});

const StyledImage = styled.img({
  width: '100%',
  height: pxToRem(180),
  minHeight: pxToRem(180),
  maxHeight: pxToRem(180),
  flexShrink: 0,
  objectFit: 'cover',
  objectPosition: 'center',
});

const TopIconsWrapper = styled.div({
  position: 'absolute',
  top: pxToRem(8),
  left: pxToRem(8),
  padding: pxToRem(8),
  zIndex: 10,
});
